'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const milestones = [
  { year: '2012', event: 'First classical Hindustani vocal recital at NCPA Pune' },
  { year: '2015', event: "Published debut poetry collection 'Echoes of Silence'" },
  { year: '2018', event: 'Launched Personality Development coaching practice' },
  { year: '2021', event: "TEDx speaker — 'Finding Your Authentic Voice'" },
  { year: '2024', event: '500+ coaching clients across 14 countries' },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef?.current?.querySelectorAll('.will-animate');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" className="py-32 lg:py-40 overflow-hidden" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="flex items-center gap-6 mb-20">
          <span className="section-label scroll-reveal will-animate">The Story</span>
          <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
        </div>

        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left — Image stack */}
          <div className="lg:col-span-5 relative scroll-reveal-left will-animate">
            <div className="relative">
              {/* Main image */}
              <div
                className="relative rounded-[32px] overflow-hidden img-hover-scale"
                style={{
                  aspectRatio: '3/4',
                  boxShadow: '0 24px 64px rgba(28,25,23,0.12)',
                }}
              >
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_11bf3155f-1772834285573.png"
                  alt="Kavita Thakur performing on stage with expressive gestures during a live music performance"
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 500px"
                />

                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to top, rgba(28,25,23,0.3) 0%, transparent 60%)',
                  }}
                />
              </div>

              {/* Floating accent image */}
              <div
                className="absolute -bottom-12 -right-8 w-48 h-56 rounded-[24px] overflow-hidden border-4 border-cream img-hover-scale hidden lg:block"
                style={{ boxShadow: '0 16px 40px rgba(28,25,23,0.18)' }}
              >
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_105b0a6e7-1772146668385.png"
                  alt="Kavita Thakur writing at a wooden desk surrounded by books and a warm lamp"
                  fill
                  className="object-cover"
                  sizes="200px"
                />
              </div>

              {/* TEDx badge */}
              <div
                className="absolute top-6 -right-4 glass-card rounded-2xl px-4 py-3 hidden lg:block"
                style={{ transform: 'rotate(3deg)' }}
              >
                <p className="text-10 tracking-widest uppercase text-gold font-bold">
                  TEDx Speaker
                </p>
                <p className="font-fraunces text-sm text-charcoal mt-0.5">
                  &apos;Finding Your Voice&apos;
                </p>
              </div>
            </div>
          </div>

          {/* Right — Text + Timeline */}
          <div className="lg:col-span-7 scroll-reveal-right will-animate">
            <h2
              className="font-fraunces font-light leading-tight mb-8"
              style={{ fontSize: 'clamp(2.5rem, 4vw, 4.5rem)' }}
            >
              A life lived in <br />
              <em className="italic">multiple octaves.</em>
            </h2>

            <p className="text-muted text-base leading-relaxed mb-6 max-w-xl">
              Born in Pune and raised between classical ragas and modern poetry, Kavita Thakur has
              spent two decades weaving together the threads of music, literature, and human
              potential. Her journey began at the Gandharva Mahavidyalaya, where she trained in
              Hindustani classical vocals under Pandit Rajan Mishra&apos;s lineage.
            </p>

            <p className="text-muted text-base leading-relaxed mb-12 max-w-xl">
              Today, she channels that same depth of practice into her coaching work — helping
              professionals, students, and artists find their authentic voice, command presence, and
              communicate with genuine impact. Her method is rooted in the belief that artistic
              discipline and personal growth are inseparable.
            </p>

            {/* Timeline */}
            <div className="space-y-0">
              {milestones?.map((m, i) => (
                <div
                  key={m?.year}
                  className="flex gap-6 pb-7 relative"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  {/* Line */}
                  {i < milestones?.length - 1 && (
                    <div
                      className="absolute left-[29px] top-8 bottom-0 w-px"
                      style={{ background: 'var(--border)' }}
                    />
                  )}

                  {/* Dot */}
                  <div className="flex-shrink-0 relative">
                    <div
                      className="w-[14px] h-[14px] rounded-full border-2 mt-1"
                      style={{
                        borderColor: 'var(--gold)',
                        background: i === milestones?.length - 1 ? 'var(--gold)' : 'var(--cream)',
                      }}
                    />
                  </div>

                  <div>
                    <span className="text-10 font-bold tracking-widest uppercase text-gold">
                      {m?.year}
                    </span>
                    <p className="text-sm text-charcoal mt-1 leading-snug">{m?.event}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() =>
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
              }
              className="btn-primary mt-4"
            >
              Work With Kavita
              <Icon name="ArrowRightIcon" size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
